import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  BotOff,
  CalendarClock,
  Eye,
  History,
  Loader2,
  Plus,
  Library,
  Rocket,
  Sparkles,
  ThumbsUp,
} from "lucide-react";
import { MarkdownEditor } from "@/components/kb/MarkdownEditor";
import { Feature, Permission, useSession, useZone } from "@/components/session-provider";
import { formatDate } from "@/lib/time";
import { ApiError } from "@/lib/api";
import { getReference, type ReferenceTeam } from "@/lib/tickets";
import { sameUserId, searchRequesterCandidates, type RequesterCandidate } from "@/lib/people";
import {
  CONTENT_TYPE_LABELS,
  STATUS_LABELS,
  VISIBILITY_LABELS,
  archiveArticle,
  createArticle,
  deleteArticle,
  getAuthorArticle,
  getKnowledgeHome,
  getKnowledgeRollout,
  listAuthorArticles,
  markArticleReviewed,
  publishArticle,
  saveArticle,
  setKnowledgeRollout,
  type ArticleContentType,
  type ArticleStatus,
  type ArticleVisibility,
  type ArticleHealth,
  type AuthorArticle,
  type KnowledgeCollection,
  type KnowledgeRollout,
  type ArticleVersion,
  type KnowledgeRolloutMode,
  FEEDBACK_REASON_LABELS,
  getArticleVersions,
  restoreArticleVersion,
  listManagedCollections,
  createManagedCollection,
  renameManagedCollection,
  setManagedCollectionActive,
  type KnowledgeCollectionAdmin,
  assistArticle,
  type ArticleAssistResult,
} from "@/lib/knowledge";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/kb/manage")({ component: ManageKnowledge });

const CONTENT_TYPES: ArticleContentType[] = ["HowTo", "Troubleshooting", "Reference"];

// ⚠️ Every member of `ArticleStatus`, in lifecycle order, and nothing else. A value the API has
// never had is D-60's phantom `Triage`: an option in a dropdown that fails to bind on submit and
// is reported to the reader as "nothing matches". This filter only ever narrows a list already
// in the browser, so a drift here shows an empty list rather than a wrong one — still worth
// keeping exact.
const STATUSES: ArticleStatus[] = ["Draft", "InReview", "Published", "Archived"];

/**
 * ⚠️ **A pilot audience names TEAMS here and not individual people, and that is a scope choice
 * rather than a limit of the model.** The domain and both enforcement layers accept named users
 * too; what a user picker would need is a staff lookup, and every one of those in this product
 * is fenced — `/api/people` behind `user.manage`, the requester picker bounded to three
 * characters and twenty results for exactly that reason (D-51). Handing an unfenced one to
 * every article author to populate a pilot is a decision of its own, and a team is the natural
 * unit for piloting a document anyway. Teams come from `/api/reference`, which already lists
 * every team to any authenticated caller, so this discloses nothing new.
 */
const VISIBILITIES: ArticleVisibility[] = ["Everyone", "Audience", "DeskOnly"];

const ROLLOUT_MODES: KnowledgeRolloutMode[] = ["Off", "Pilot", "Everyone"];

function ManageKnowledge() {
  const { can, hasFeature, user } = useSession();
  const zone = useZone();

  const [articles, setArticles] = useState<AuthorArticle[] | null>(null);
  const [collections, setCollections] = useState<KnowledgeCollection[]>([]);
  const [teams, setTeams] = useState<ReferenceTeam[]>([]);
  const [editing, setEditing] = useState<AuthorArticle | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [onlyReviewDue, setOnlyReviewDue] = useState(false);

  // ⚠️ Filters over a list the browser ALREADY HOLDS — no fetch, no rows, no PHI read — which
  // is what makes filtering as you type safe here and not on the queue, where every row a
  // search returns files a read against the caller's name (D-78, D-140's distinction).
  const [teamFilter, setTeamFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  // ⚠️ One predicate, read by the banner's count, the filter and the badge. Three copies of
  // "is this stale" is how a screen comes to say four and list five.
  const isReviewDue = (article: AuthorArticle) =>
    article.reviewDueAtUtc !== null && new Date(article.reviewDueAtUtc) <= new Date();

  const all = articles ?? [];
  const reviewDue = all.filter(isReviewDue).length;
  const shown = all.filter(
    (a) =>
      (!onlyReviewDue || isReviewDue(a)) &&
      (teamFilter === "" || a.owningTeamId === teamFilter) &&
      (statusFilter === "" || a.status === statusFilter),
  );
  const filtered = all.length - shown.length;

  /**
   * Why an article cannot be created right now, or null.
   *
   * ⚠️ **A REASON, NEVER A BARE BOOLEAN** (D-123). The button was `disabled` on
   * `collections.length === 0 || teams.length === 0` with nothing on screen saying which, so a
   * press did nothing and the two causes — no shelf to file it on, no team to own it — need
   * opposite corrections. A disabled control that states no reason is the defect D-122 records:
   * the reader cannot tell a refusal from a failure from an absence.
   */
  const blockedReason =
    collections.length > 0 && teams.length > 0
      ? null
      : failed !== null
        ? "The library could not be loaded, so nothing can be filed yet. The message above says why."
        : collections.length === 0
          ? can(Permission.KnowledgeManage)
            ? "There are no collections yet. An article has to sit on a shelf, so create one in the Collections panel just below."
            : "There are no collections yet. An article has to sit on a shelf, and creating one needs an administrator."
          : "No active teams are configured, and an article has to be owned by one.";

  const refresh = useCallback(async () => {
    try {
      const [list, home, reference] = await Promise.all([
        listAuthorArticles(),
        getKnowledgeHome(),
        getReference(),
      ]);
      setArticles(list);
      setCollections(home.collections);
      setTeams(reference.teams);
      setFailed(null);
    } catch (error: unknown) {
      // ⚠️ A failure renders as a failure, never as an empty list — "you have written nothing"
      // and "the request was refused" are different facts (D-122).
      setFailed(
        error instanceof ApiError ? error.message : "The article list could not be loaded.",
      );
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!hasFeature(Feature.KnowledgeBase) || !(user?.canAuthorKnowledge ?? false)) {
    // Rendering only. Every route re-checks and answers 404.
    return (
      <div className="mx-auto w-full max-w-3xl p-6">
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-[13px] font-medium">This screen is not available to you.</p>
        </div>
      </div>
    );
  }

  if (editing !== null) {
    return (
      <ArticleEditor
        article={editing}
        collections={collections}
        teams={teams}
        onClose={() => {
          setEditing(null);
          void refresh();
        }}
      />
    );
  }

  /**
   * Creates a blank draft and opens it directly in the editor — no intermediate form.
   *
   * ⚠️ Both the shelf and the owning team default to the first entry now, the same treatment.
   * That used to be true only of the shelf — a shelf is an arrangement and is freely correctable
   * from the editor's "Collection" field, while the owning team is what `MayAuthor` is checked
   * against, and guessing it wrong used to mean asking first. It is now correctable the same
   * way, from the editor's "Maintained by" field, before the first save — the trade this screen
   * makes for going straight to the editor instead of asking up front. If the guess is one this
   * author may not write for, `createArticle` is refused and the failure banner says so, exactly
   * as a wrong choice in the old form would have.
   */
  const createNew = async () => {
    if (collections.length === 0 || teams.length === 0) return;

    setFailed(null);
    setBusy(true);
    try {
      const article = await createArticle({
        collectionId: collections[0].id,
        owningTeamId: teams[0].id,
        // The editor opens straight onto the title field, so a placeholder is enough here —
        // nobody types a title into a form they have not seen yet.
        title: "New article",
        contentType: "HowTo",
      });
      setEditing(article);
    } catch (error: unknown) {
      setFailed(error instanceof ApiError ? error.message : "That article could not be created.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">Knowledge base articles</h1>
          <p className="text-[13px] text-muted-foreground">
            Write and publish the answers people can use instead of raising a request.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Link
            to="/kb"
            className="rounded-md border border-border px-3 py-1.5 text-[12px] font-medium hover:bg-muted"
          >
            View the library
          </Link>
          <button
            type="button"
            onClick={() => void createNew()}
            disabled={busy || blockedReason !== null}
            title={blockedReason ?? undefined}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground disabled:opacity-50"
          >
            <Plus className="size-3.5" aria-hidden />
            New article
          </button>
        </div>
      </header>

      {/* ⚠️ Rendered, never only a `title` tooltip: a reason that needs a mouse hover is a
          reason nobody on a phone can reach, and this is the sentence that says which of the
          two causes it is. */}
      {blockedReason !== null ? (
        <p className="rounded-md border border-border bg-muted/40 p-3 text-[12px] text-muted-foreground">
          {blockedReason}
        </p>
      ) : null}

      {/* ⚠️ ORDERING, not new logic. With no shelves, creating one is the ONLY action on this
          screen that does anything — and it sat at the very bottom, below an empty article list
          and the rollout panel. On a phone that is a long scroll past two things that cannot be
          acted on, which is indistinguishable from a screen that is simply broken. Where there
          is exactly one useful action, it goes first. */}
      {can(Permission.KnowledgeManage) && collections.length === 0 ? (
        <CollectionsPanel onChanged={() => void refresh()} />
      ) : null}

      {failed !== null ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-[12px]">
          {failed}
        </p>
      ) : null}

      {/* ⚠️ The KCS discipline, and the reason ReviewDueAtUtc exists: an article confidently
          describing last year's Intune policy is worse than no article, because somebody
          follows it. Counted rather than only badged, so the figure is visible before anybody
          scrolls — a stale library's problem is that nothing about it looks wrong. */}
      {reviewDue > 0 ? (
        <button
          type="button"
          onClick={() => setOnlyReviewDue((on) => !on)}
          className={cn(
            "flex w-full items-center gap-2 rounded-md border p-3 text-left text-[12px]",
            onlyReviewDue
              ? "border-amber-500/50 bg-amber-500/10"
              : "border-border hover:bg-muted/50",
          )}
        >
          <CalendarClock className="size-4 text-amber-600 dark:text-amber-400" aria-hidden />
          <span className="flex-1">
            <strong className="font-medium">
              {reviewDue === 1 ? "1 article is" : `${reviewDue} articles are`} due for review
            </strong>{" "}
            — somebody should check they are still true.
          </span>
          <span className="text-muted-foreground">{onlyReviewDue ? "Show all" : "Show these"}</span>
        </button>
      ) : null}

      {articles === null ? (
        <p className="text-[13px] text-muted-foreground">Loading…</p>
      ) : articles.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-[13px] font-medium">Nothing written yet</p>
          <p className="mx-auto mt-1 max-w-md text-[12px] text-muted-foreground">
            It arrives as a draft for you to write, and nobody else sees it until it is published.
          </p>
          <button
            type="button"
            onClick={() => void createNew()}
            disabled={busy || blockedReason !== null}
            title={blockedReason ?? undefined}
            className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground disabled:opacity-50"
          >
            <Plus className="size-3.5" aria-hidden />
            New article
          </button>
        </div>
      ) : (
        <>
          {/* ⚠️ Filters a list already in the browser, so this costs no request and audits
              nothing — the reason per-keystroke narrowing is safe here and not on the queue
              (D-78, D-140). Both narrow on top of what the server already decided this author
              may see; neither can widen it. */}
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border p-3">
            <Field label="Team">
              <select
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                className="rounded-md border border-border bg-background px-2 py-1.5 text-base sm:text-[12px]"
              >
                <option value="">Every team</option>
                {/* ⚠️ Built from the ARTICLES, not from the reference list: offering a team
                    that owns nothing here is a filter whose only outcome is an empty list. */}
                {[...new Map(all.map((a) => [a.owningTeamId, a.owningTeamName])).entries()]
                  .sort((a, b) => a[1].localeCompare(b[1]))
                  .map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
              </select>
            </Field>

            <Field label="Status">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-md border border-border bg-background px-2 py-1.5 text-base sm:text-[12px]"
              >
                <option value="">Any status</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </Field>

            {/* The denominator, on screen (D-53): a filtered list that does not say how much it
                is hiding reads as the whole library. */}
            <p className="ml-auto text-[11px] text-muted-foreground">
              Showing {shown.length} of {all.length}
              {filtered > 0 ? ` · ${filtered} hidden by filters` : ""}
            </p>

            {teamFilter !== "" || statusFilter !== "" || onlyReviewDue ? (
              <button
                type="button"
                onClick={() => {
                  setTeamFilter("");
                  setStatusFilter("");
                  setOnlyReviewDue(false);
                }}
                className="rounded-md border border-border px-2.5 py-1 text-[12px] hover:bg-muted"
              >
                Clear filters
              </button>
            ) : null}
          </div>

          {/* ⚠️ "Nothing matches these filters" and "nothing has been written" are different
              facts, and collapsing them is what made a refused request read as an empty
              library elsewhere in this product (D-122). */}
          {shown.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-8 text-center">
              <p className="text-[13px] font-medium">No articles match these filters</p>
              <p className="mt-1 text-[12px] text-muted-foreground">
                {all.length} {all.length === 1 ? "article is" : "articles are"} written; none of
                them matches what is selected above.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {shown.map((article) => (
                <li key={article.id}>
                  <button
                    type="button"
                    onClick={() => void openArticle(article.id, setEditing, setFailed)}
                    className="block w-full p-4 text-left transition-colors hover:bg-muted/50"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[13px] font-medium">{article.title}</span>
                      <StatusPill status={article.status} />
                      {article.visibility !== "Everyone" ? (
                        <span className="rounded border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">
                          {VISIBILITY_LABELS[article.visibility]}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
                      <span>{CONTENT_TYPE_LABELS[article.contentType]}</span>
                      <span>· {article.owningTeamName}</span>
                      <span>· Updated {formatDate(article.updatedAtUtc, zone)}</span>
                      {isReviewDue(article) ? (
                        <span className="text-amber-600 dark:text-amber-400">· Review due</span>
                      ) : null}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {can(Permission.KnowledgeManage) && collections.length > 0 ? (
        <CollectionsPanel onChanged={() => void refresh()} />
      ) : null}

      {can(Permission.KnowledgeManage) ? <RolloutPanel /> : null}
    </div>
  );
}

async function openArticle(
  id: string,
  setEditing: (article: AuthorArticle) => void,
  setFailed: (message: string) => void,
) {
  try {
    setEditing(await getAuthorArticle(id));
  } catch (error: unknown) {
    setFailed(error instanceof ApiError ? error.message : "That article could not be opened.");
  }
}

function StatusPill({ status }: { status: ArticleStatus }) {
  return (
    <span
      className={cn(
        "rounded px-1.5 py-0.5 text-[11px] font-medium",
        status === "Published"
          ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
          : status === "Archived"
            ? "bg-muted text-muted-foreground"
            : "bg-amber-500/10 text-amber-700 dark:text-amber-400",
      )}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

function ArticleEditor({
  article,
  collections,
  teams,
  onClose,
}: {
  article: AuthorArticle;
  collections: KnowledgeCollection[];
  teams: ReferenceTeam[];
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(article);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // ⚠️ Filters over a list the browser ALREADY HOLDS — `teams` came back with `getReference()`
  // on load, so narrowing it here is no fetch, no rows and no PHI read, exactly the reasoning
  // `teamFilter`/`statusFilter` already rest on above for the article list (D-140's distinction
  // between this and a search that returns rows, like D-78's).
  const [categorySearch, setCategorySearch] = useState("");
  const [categoryTeamFilter, setCategoryTeamFilter] = useState("");

  const categories = useMemo(
    () =>
      teams.flatMap((team) =>
        team.categories.map((c) => ({ ...c, team: team.name, teamId: team.id })),
      ),
    [teams],
  );

  const visibleCategories = categories.filter(
    (c) =>
      (categoryTeamFilter === "" || c.teamId === categoryTeamFilter) &&
      (categorySearch.trim() === "" ||
        c.name.toLowerCase().includes(categorySearch.trim().toLowerCase())),
  );

  const run = async (action: () => Promise<unknown>, then?: () => void) => {
    setBusy(true);
    setFailed(null);
    try {
      await action();
      then?.();
    } catch (error: unknown) {
      // ⚠️ The SERVER'S OWN SENTENCE where it sent one. A refusal to publish an article limited
      // to an empty pilot audience is exactly what the person needs to read; replacing it with
      // "something went wrong" throws away the only line saying what to do.
      setFailed(error instanceof ApiError ? error.message : "That change could not be applied.");
    } finally {
      setBusy(false);
    }
  };

  // ⚠️ The raw call, kept separate from `run()` on purpose. `run()` catches whatever it is
  // given and reports it through `failed` rather than rethrowing, which is right for a
  // button that only saves — but wrong for a step inside a longer sequence: chaining
  // `run(() => save())` swallowed a failed save and let the step after it run anyway,
  // which is how a rejected save (bad title, an audience member that could not be read) was
  // followed a moment later by a publish attempt on whatever had last been persisted.
  const saveDraft = () =>
    saveArticle(draft.id, {
      title: draft.title,
      summary: draft.summary,
      body: draft.body,
      contentType: draft.contentType,
      visibility: draft.visibility,
      collectionId: draft.collectionId,
      owningTeamId: draft.owningTeamId,
      appliesTo: draft.appliesTo,
      keywords: draft.keywords,
      categoryIds: draft.categoryIds,
      audience: draft.audience,
    });

  const save = () =>
    run(saveDraft, () => {
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    });

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          All articles
        </button>

        <div className="flex flex-wrap items-center gap-2">
          {saved ? <span className="text-[12px] text-muted-foreground">Saved</span> : null}
          <StatusPill status={draft.status} />

          <button
            type="button"
            onClick={() => void save()}
            disabled={busy}
            className="rounded-md border border-border px-3 py-1.5 text-[12px] font-medium hover:bg-muted disabled:opacity-50"
          >
            Save draft
          </button>

          {/* ⚠️ `canPublish` comes from the SERVER, per article and per owning team — it is not
              `can("kb.publish")`, which reaches the browser without scope and would be true for
              somebody who holds it on a different team entirely. The D-51 trap. */}
          {draft.canPublish ? (
            <button
              type="button"
              onClick={() =>
                void run(async () => {
                  await saveDraft();
                  await publishArticle(draft.id);
                }, onClose)
              }
              disabled={busy}
              className="rounded-md bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground disabled:opacity-50"
            >
              {draft.status === "Published" ? "Publish changes" : "Publish"}
            </button>
          ) : null}
        </div>
      </div>

      {failed !== null ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-[12px]">
          {failed}
        </p>
      ) : null}

      {draft.visibility === "Everyone" &&
      draft.status !== "Published" &&
      draft.contentType !== "Troubleshooting" ? (
        <p className="rounded-md border border-border bg-muted/40 p-3 text-[12px] text-muted-foreground">
          Publishing makes this readable by everyone the knowledge base has been switched on for.
          Check there is nothing in it about a specific person or patient before you do.
        </p>
      ) : null}

      <div className="space-y-4">
        <Field label="Title">
          <input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-base sm:text-[13px]"
          />
        </Field>

        <Field
          label="Summary"
          hint="The line under the title, and the line a search result is read from."
        >
          <input
            value={draft.summary ?? ""}
            onChange={(e) => setDraft({ ...draft, summary: e.target.value || null })}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-base sm:text-[13px]"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Kind">
            <select
              value={draft.contentType}
              onChange={(e) =>
                setDraft({ ...draft, contentType: e.target.value as ArticleContentType })
              }
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-base sm:text-[13px]"
            >
              {CONTENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {CONTENT_TYPE_LABELS[type]}
                </option>
              ))}
            </select>

            {/* ⚠️ Not for requesters, and this is the one place that says why, in full — the
                dropdown option and the article-list badge both carry the shorter "(AI only)"
                form of the same fact (CONTENT_TYPE_LABELS). Requesters never reach a
                Troubleshooting article at all: KnowledgeEndpoints.Live excludes the content
                type outright, for every reader, regardless of this article's own visibility. */}
            {draft.contentType === "Troubleshooting" && (
              <p className="mt-2 flex items-start gap-1.5 rounded-md border border-warning/40 bg-warning/10 px-2.5 py-2 text-xs text-warning">
                <BotOff className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>
                  Requesters never see this. TARS, the ticket-assistant chat, reads it as internal
                  troubleshooting instructions for the categories below — write it TO the assistant,
                  not to the person raising the request.
                </span>
              </p>
            )}
          </Field>

          <Field label="Who can read it" hint="Once it is published.">
            {/* ⚠️ Troubleshooting is never reader-facing — KnowledgeEndpoints.Live excludes the
                content type outright for every reader, regardless of whatever this article's
                Visibility is set to (see the warning under Kind, above). So the picker is
                replaced rather than merely defaulted: showing "Everyone" here while the article
                is in fact readable by nobody but TARS is the exact defect D-122 names — a
                control describing an outcome the server does not produce. The stored Visibility
                value is left untouched underneath, in case the author later changes the Kind
                back and the earlier choice should still be there. */}
            {draft.contentType === "Troubleshooting" ? (
              <div className="flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-[13px] text-muted-foreground">
                <BotOff className="size-3.5 shrink-0" aria-hidden />
                AI only — TARS reads this. Requesters and agents never see it, whatever this is set
                to.
              </div>
            ) : (
              <select
                value={VISIBILITIES.includes(draft.visibility) ? draft.visibility : "Everyone"}
                onChange={(e) =>
                  setDraft({ ...draft, visibility: e.target.value as ArticleVisibility })
                }
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-base sm:text-[13px]"
              >
                {VISIBILITIES.map((visibility) => (
                  <option key={visibility} value={visibility}>
                    {VISIBILITY_LABELS[visibility]}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Collection">
            <select
              value={draft.collectionId}
              onChange={(e) => setDraft({ ...draft, collectionId: e.target.value })}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-base sm:text-[13px]"
            >
              {collections.map((collection) => (
                <option key={collection.id} value={collection.id}>
                  {collection.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Maintained by" hint="Whose review queue it lands in when it goes stale.">
            <select
              value={draft.owningTeamId}
              onChange={(e) => setDraft({ ...draft, owningTeamId: e.target.value })}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-base sm:text-[13px]"
            >
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Applies to" hint="Windows 11 · Microsoft 365 E3 · Avatar · Kipu">
            <input
              value={draft.appliesTo ?? ""}
              onChange={(e) => setDraft({ ...draft, appliesTo: e.target.value || null })}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-base sm:text-[13px]"
            />
          </Field>

          <Field
            label="Keywords"
            hint="The words people type, not the words we use — “can't log in”, not “authentication failure”."
          >
            <input
              value={draft.keywords ?? ""}
              onChange={(e) => setDraft({ ...draft, keywords: e.target.value || null })}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-base sm:text-[13px]"
            />
          </Field>
        </div>

        {draft.visibility === "Audience" && draft.contentType !== "Troubleshooting" ? (
          <Field
            label="The pilot"
            hint="Only these teams will see it once it is published. Publishing with nobody named is refused — an article reaching nobody is live, counted, and readable by not one person."
          >
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border p-2">
              {teams.map((team) => {
                const named = draft.audience.some(
                  (m) => m.kind === "Team" && m.subjectId === team.id,
                );

                return (
                  <label key={team.id} className="flex items-center gap-2 text-[12px]">
                    <input
                      type="checkbox"
                      checked={named}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          audience: e.target.checked
                            ? [...draft.audience, { kind: "Team", subjectId: team.id }]
                            : draft.audience.filter(
                                (m) => !(m.kind === "Team" && m.subjectId === team.id),
                              ),
                        })
                      }
                    />
                    {team.name}
                  </label>
                );
              })}
            </div>
          </Field>
        ) : null}

        <Field
          label="Related request categories"
          hint="Offered to somebody about to raise a request in one of these. A topic tag only — it does not change who can read the article."
        >
          <div className="mb-1.5 flex flex-wrap gap-2">
            <input
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              placeholder="Search categories…"
              className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-base sm:text-[12px]"
            />
            <select
              value={categoryTeamFilter}
              onChange={(e) => setCategoryTeamFilter(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1 text-base sm:text-[12px]"
            >
              <option value="">All teams</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border p-2">
            {visibleCategories.length === 0 ? (
              <p className="p-1 text-[12px] text-muted-foreground">
                No categories match{" "}
                {categorySearch.trim() !== "" ? `"${categorySearch.trim()}"` : "this filter"}.
              </p>
            ) : (
              visibleCategories.map((category) => (
                <label key={category.id} className="flex items-center gap-2 text-[12px]">
                  <input
                    type="checkbox"
                    checked={draft.categoryIds.includes(category.id)}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        categoryIds: e.target.checked
                          ? [...draft.categoryIds, category.id]
                          : draft.categoryIds.filter((id) => id !== category.id),
                      })
                    }
                  />
                  {category.name}
                  <span className="text-muted-foreground">· {category.team}</span>
                </label>
              ))
            )}
          </div>
        </Field>

        <Field label="Article">
          <MarkdownEditor
            value={draft.body}
            onChange={(body) => setDraft({ ...draft, body })}
            disabled={busy}
          />
        </Field>

        <ArticleAssistantPanel
          contentType={draft.contentType}
          onContentTypeChange={(contentType) => setDraft({ ...draft, contentType })}
          existing={draft.body}
          onApply={(fields) => setDraft({ ...draft, ...fields })}
        />
      </div>

      {draft.health !== null ? <HealthPanel health={draft.health} /> : null}

      <VersionsPanel
        articleId={draft.id}
        canRestore={draft.canPublish || draft.status !== "Published"}
        onRestored={setDraft}
      />

      <div className="flex flex-wrap gap-2 border-t border-border pt-4">
        {draft.canPublish && draft.status === "Published" ? (
          <>
            <button
              type="button"
              onClick={() => void run(() => markArticleReviewed(draft.id), onClose)}
              disabled={busy}
              className="rounded-md border border-border px-3 py-1.5 text-[12px] hover:bg-muted disabled:opacity-50"
            >
              Still correct — reset the review date
            </button>
            <button
              type="button"
              onClick={() => void run(() => archiveArticle(draft.id), onClose)}
              disabled={busy}
              className="rounded-md border border-border px-3 py-1.5 text-[12px] hover:bg-muted disabled:opacity-50"
            >
              Take it down
            </button>
          </>
        ) : null}

        {draft.canPublish ? (
          <button
            type="button"
            onClick={() => void run(() => deleteArticle(draft.id), onClose)}
            disabled={busy}
            className="ml-auto rounded-md border border-destructive/40 px-3 py-1.5 text-[12px] text-destructive hover:bg-destructive/5 disabled:opacity-50"
          >
            Delete
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * How the article is doing.
 *
 * ⚠️ **Counts, and not one of them names anybody.** There is no reader list here and no way to
 * ask for one — the tables behind these figures have no user column at all. That is the refusal
 * D-69, D-70, D-108, D-118 and D-119 each made, held by the shape of the data rather than by
 * this component choosing not to render something.
 *
 * ⚠️ A rate below five answers is **withheld as "too few to publish", never shown as a small
 * percentage** — "100% helpful" over one answer is a ranking by sample size (D-130). The counts
 * stay exact, because they are the only thing separating "nobody answered" from "too few".
 */
function HealthPanel({ health }: { health: ArticleHealth }) {
  return (
    <section className="space-y-3 rounded-lg border border-border p-4">
      <h2 className="text-[13px] font-semibold">How it is doing</h2>

      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div>
          <dt className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Eye className="size-3" aria-hidden />
            Opened, last 30 days
          </dt>
          <dd className="text-lg font-semibold tabular-nums">{health.views30}</dd>
        </div>

        <div>
          <dt className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ThumbsUp className="size-3" aria-hidden />
            Found it helpful
          </dt>
          <dd className="text-lg font-semibold tabular-nums">
            {health.helpfulRate !== null ? `${Math.round(health.helpfulRate * 100)}%` : "—"}
          </dd>
          {/* ⚠️ The denominator is always on screen (D-53), and it is what makes a withheld
              rate readable rather than mysterious. */}
          <dd className="text-[11px] text-muted-foreground">
            {health.helpful + health.notHelpful === 0
              ? "Nobody has answered yet"
              : health.helpfulRate === null
                ? `${health.helpful + health.notHelpful} answers — too few to publish a rate`
                : `${health.helpful} of ${health.helpful + health.notHelpful} answers`}
          </dd>
        </div>

        {/* ⚠️ Only drawn once something has been measured — while ai.kb-deflection stays
            locked off (D-174), this is a permanent zero on every article, and a stat cell
            that can never move is furniture. */}
        {health.deflectionShownCount > 0 && (
          <div>
            <dt className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Sparkles className="size-3" aria-hidden />
              Suggested before a request
            </dt>
            <dd className="text-lg font-semibold tabular-nums">
              {health.deflectionAnsweredCount} of {health.deflectionShownCount}
            </dd>
            <dd className="text-[11px] text-muted-foreground">answered it, no request raised</dd>
          </div>
        )}
      </dl>

      {health.reasons.length > 0 ? (
        <div className="space-y-1">
          <p className="text-[11px] font-medium text-muted-foreground">
            What people said was wrong with it
          </p>
          <ul className="space-y-0.5">
            {health.reasons.map((reason) => (
              <li key={reason.reason} className="flex justify-between text-[12px]">
                <span>{FEEDBACK_REASON_LABELS[reason.reason]}</span>
                <span className="tabular-nums text-muted-foreground">{reason.count}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="text-[11px] text-muted-foreground">
        Nothing here records who read the article or who answered — these are counts, and the tables
        behind them hold no names.
      </p>
    </section>
  );
}

/**
 * What it said before.
 *
 * ⚠️ **Restoring puts the WORDS back and publishes nothing.** Rolling back is two deliberate
 * acts — restore, then publish — because an undo that silently republished is the shape where a
 * mis-click puts month-old instructions in front of everybody.
 */
function VersionsPanel({
  articleId,
  canRestore,
  onRestored,
}: {
  articleId: string;
  canRestore: boolean;
  onRestored: (article: AuthorArticle) => void;
}) {
  const zone = useZone();
  const [versions, setVersions] = useState<ArticleVersion[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setOpen(true);
    if (versions !== null) return;
    try {
      setVersions(await getArticleVersions(articleId));
    } catch {
      setVersions([]);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => void load()}
        className="flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground"
      >
        <History className="size-3.5" aria-hidden />
        What it said before
      </button>
    );
  }

  return (
    <section className="space-y-2 rounded-lg border border-border p-4">
      <h2 className="flex items-center gap-1.5 text-[13px] font-semibold">
        <History className="size-3.5" aria-hidden />
        What it said before
      </h2>

      {versions === null ? (
        <p className="text-[12px] text-muted-foreground">Loading…</p>
      ) : versions.length === 0 ? (
        <p className="text-[12px] text-muted-foreground">
          It has never been published, so there is nothing to go back to.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {versions.map((version) => (
            <li key={version.number} className="flex items-center gap-3 py-2">
              <span className="text-[12px] tabular-nums text-muted-foreground">
                v{version.number}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12px]">{version.title}</span>
              <span className="text-[11px] text-muted-foreground">
                {formatDate(version.publishedAtUtc, zone)}
              </span>
              {canRestore ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    void restoreArticleVersion(articleId, version.number)
                      .then(onRestored)
                      .finally(() => setBusy(false));
                  }}
                  className="rounded border border-border px-2 py-0.5 text-[11px] hover:bg-muted disabled:opacity-50"
                >
                  Put this text back
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <p className="text-[11px] text-muted-foreground">
        Restoring only changes the draft — it publishes nothing.
      </p>
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-[12px] font-medium">{label}</span>
      {hint ? <span className="block text-[11px] text-muted-foreground">{hint}</span> : null}
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// The rollout — who the knowledge base exists for
// ---------------------------------------------------------------------------

/**
 * ⚠️ **This is the switch that hides the menu, and it is NOT the feature flag.** The flag in
 * Settings is the kill switch: off and the knowledge base is gone for everybody including its
 * authors. This is the audience — who among ordinary users can see a feature that is switched
 * on and working. The desk reaches the library whatever this says, because somebody has to
 * write the articles before anybody is told the library exists.
 */
/**
 * Drafting or reworking an article with the assistant (D-5).
 *
 * ⚠️ `ArticleAssistantPanel`, not `AssistantPanel` — the ticket-intake assistant is now the
 * conversational `SupportAgentChat` in `components/support-agent/`, but the naming discipline
 * stands: two components with one name in a product with two assistants is how somebody reads
 * the wrong file while deciding what a model is allowed to see.
 *
 * ⚠️ **NOTHING IT RETURNS IS SAVED, and every field is applied by a separate press.** The
 * suggestion lands beside the editor, the author reads it, and takes the parts they want. A model
 * that wrote straight into the draft would be a model whose output somebody publishes without
 * having read it — which is the failure that makes a knowledge base worse than none, because a
 * confidently invented step reads exactly like a correct one.
 *
 * ⚠️ **The body is markdown and goes through the same parser as anything an author types.**
 * `parseArticle` is total, so a model that emitted a script tag would produce a paragraph
 * containing those characters. Nothing was added to make this safe — the parser was already the
 * reason `dangerouslySetInnerHTML` is banned and the CSP needs no sanitiser behind it.
 *
 * ⚠️ **It is drawn only when the SERVER says so.** `canUseKnowledgeAssistant` is the flag AND a
 * configured model, computed from the same pair the endpoint checks — a button that answers 404
 * would read as the assistant being broken rather than deliberately switched off.
 */
function ArticleAssistantPanel({
  contentType,
  onContentTypeChange,
  existing,
  onApply,
}: {
  contentType: ArticleContentType;
  onContentTypeChange: (contentType: ArticleContentType) => void;
  existing: string;
  onApply: (fields: Partial<AuthorArticle>) => void;
}) {
  const { user } = useSession();
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [referenceUrl, setReferenceUrl] = useState("");
  const [result, setResult] = useState<ArticleAssistResult | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (user?.canUseKnowledgeAssistant !== true) return null;

  const rework = existing.trim().length > 0;

  const ask = async () => {
    setBusy(true);
    setFailed(null);
    setResult(null);
    try {
      setResult(
        await assistArticle({
          instruction,
          contentType,
          referenceUrl: referenceUrl.trim() === "" ? null : referenceUrl.trim(),
          // ⚠️ Sent only when there is something to rework. An empty string would ask the model
          // to rewrite nothing, which it obliges by inventing an article.
          existing: rework ? existing : null,
        }),
      );
    } catch (error: unknown) {
      // ⚠️ Three answers, never one (D-122). A 404 is "not available here", a 503 is "the model
      // gave nothing back and nothing was saved", and anything else — including a rejected
      // reference link — carries the server's own sentence.
      setFailed(
        error instanceof ApiError
          ? error.status === 404
            ? "The assistant is not switched on for this deployment."
            : error.message
          : "That could not be sent.",
      );
    } finally {
      setBusy(false);
    }
  };

  // ⚠️ Collapsed by default and reopened beside the field it writes into, rather than an
  // always-open block an author scrolls past — "next to the article free text field" is a
  // control they press there, not a permanent section under it.
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border px-3 py-1.5 text-[12px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <Sparkles className="size-3.5" aria-hidden />
        Write with AI
      </button>
    );
  }

  return (
    <section className="space-y-3 rounded-lg border border-dashed border-border p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-muted-foreground" aria-hidden />
          <h3 className="text-[13px] font-semibold">
            {rework ? "Rework this with the assistant" : "Draft this with the assistant"}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[11px] text-muted-foreground hover:text-foreground"
        >
          Close
        </button>
      </div>

      <p className="text-[12px] text-muted-foreground">
        Describe the article, or what you want changed. Nothing is saved — the suggestion appears
        below and you take the parts you want.{" "}
        <span className="font-medium">
          Do not describe a specific person, patient or case: write about the general problem.
        </span>
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Kind of SOP"
          hint="Shapes how the assistant structures it — same as Kind, above."
        >
          <select
            value={contentType}
            onChange={(e) => onContentTypeChange(e.target.value as ArticleContentType)}
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-base sm:text-[12px]"
          >
            {CONTENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {CONTENT_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Reference link (optional)"
          hint="Shown to the assistant as text only — never opened or fetched. Describe what matters from it below too."
        >
          <input
            value={referenceUrl}
            onChange={(e) => setReferenceUrl(e.target.value)}
            placeholder="https://docs.example.com/vpn-setup"
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-base sm:text-[12px]"
          />
        </Field>
      </div>

      <textarea
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        rows={3}
        placeholder={
          rework
            ? "Add a section about signing in from home over GlobalProtect"
            : "How to request a new starter's Microsoft 365 account and what to have ready"
        }
        className="w-full rounded-md border border-border bg-background p-2 text-base sm:text-[12px]"
      />

      <button
        type="button"
        onClick={() => void ask()}
        disabled={busy || instruction.trim().length < 20}
        className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-[12px] font-medium hover:bg-muted disabled:opacity-50"
      >
        {busy && <Loader2 className="size-3 animate-spin" aria-hidden />}
        {busy ? "Writing…" : rework ? "Suggest changes" : "Write a draft"}
      </button>

      {failed !== null ? <p className="text-[12px] text-destructive">{failed}</p> : null}

      {result !== null ? (
        <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
          <p className="text-[11px] text-muted-foreground">
            A suggestion, not a saved change. Read it before you take it — an invented step reads
            exactly like a correct one.
          </p>

          <AssistantField
            label="Title"
            value={result.title}
            onApply={() => onApply({ title: result.title })}
          />
          <AssistantField
            label="Summary"
            value={result.summary}
            onApply={() => onApply({ summary: result.summary })}
          />
          <AssistantField
            label="Keywords"
            value={result.keywords}
            onApply={() => onApply({ keywords: result.keywords })}
          />
          <AssistantField
            label="Applies to"
            value={result.appliesTo}
            onApply={() => onApply({ appliesTo: result.appliesTo })}
          />
          <AssistantField
            label="Article"
            value={result.body}
            multiline
            onApply={() => onApply({ body: result.body })}
          />
        </div>
      ) : null}
    </section>
  );
}

function AssistantField({
  label,
  value,
  multiline = false,
  onApply,
}: {
  label: string;
  value: string;
  multiline?: boolean;
  onApply: () => void;
}) {
  if (value.trim().length === 0) return null;

  return (
    <div className="rounded border border-border bg-background p-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <button
          type="button"
          onClick={onApply}
          className="rounded border border-border px-2 py-0.5 text-[11px] hover:bg-muted"
        >
          Use this
        </button>
      </div>
      {/* Plain text, deliberately. A rendered preview of a suggestion invites somebody to judge
          it by how it looks rather than by whether the steps are true. */}
      <p
        className={cn(
          "mt-1 whitespace-pre-wrap text-[12px]",
          multiline ? "max-h-64 overflow-y-auto" : "",
        )}
      >
        {value}
      </p>
    </div>
  );
}

/**
 * The shelves.
 *
 * ⚠️ **There is no delete and the server cannot express one.** A collection holds articles that
 * people link to from requests, so removing the shelf would either orphan them or take content
 * away as a side effect of a button about arrangement. Hiding it takes the shelf out of the
 * library and leaves every article reachable by its own URL — and it is reversible, which is
 * what makes the control defensible at all (D-140's shape).
 *
 * ⚠️ **A rename does not move the address, and the screen says so before anybody presses.** The
 * slug is derived once at creation; regenerating it on rename would break every link somebody
 * has pasted into a request, silently, and the first sign of it is a 404 reported as the
 * article having been deleted.
 *
 * ⚠️ **Two counts per shelf.** "6 articles" and "0 published" is the state this screen exists to
 * make visible; one figure would leave an author wondering why their shelf is empty to everyone
 * else.
 */
/**
 * @param onChanged Told after a shelf is created, renamed, hidden or put back.
 *
 * ⚠️ **Without this the screen contradicts itself.** The panel reloads its OWN list, and the
 * page above it holds a separate `collections` that decides whether an article can be created
 * at all — so creating the first shelf left "There are no collections yet" on screen and the
 * New article button still disabled, correctable only by reloading the page. Two readers of one
 * fact, and the stale one is the one carrying the refusal.
 */
function CollectionsPanel({ onChanged }: { onChanged: () => void }) {
  const [shelves, setShelves] = useState<KnowledgeCollectionAdmin[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [order, setOrder] = useState("");

  const load = useCallback(() => {
    void listManagedCollections()
      .then(setShelves)
      .catch(() => setFailed("The collections could not be loaded."));
  }, []);

  useEffect(load, [load]);

  const startNew = () => {
    setEditingId("new");
    setName("");
    setDescription("");
    setOrder("");
  };

  const startEdit = (shelf: KnowledgeCollectionAdmin) => {
    setEditingId(shelf.id);
    setName(shelf.name);
    setDescription(shelf.description ?? "");
    setOrder(String(shelf.order));
  };

  const save = async () => {
    setBusy(true);
    setFailed(null);
    try {
      const parsed = order.trim().length === 0 ? null : Number(order.trim());
      const input = {
        name,
        description: description.trim().length === 0 ? null : description.trim(),
        // ⚠️ An unreadable number is sent as "leave it alone", never as 0 — a typo in this box
        // must not silently move a shelf to the top of the library.
        order: parsed === null || Number.isNaN(parsed) ? null : parsed,
      };

      if (editingId === "new") await createManagedCollection(input);
      else if (editingId !== null) await renameManagedCollection(editingId, input);

      setEditingId(null);
      load();
      onChanged();
    } catch (error: unknown) {
      setFailed(error instanceof ApiError ? error.message : "That could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (shelf: KnowledgeCollectionAdmin, active: boolean) => {
    setBusy(true);
    setFailed(null);
    try {
      await setManagedCollectionActive(shelf.id, active);
      load();
      onChanged();
    } catch (error: unknown) {
      setFailed(error instanceof ApiError ? error.message : "That could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  if (shelves === null) {
    return failed === null ? null : (
      <section className="rounded-lg border border-border p-4">
        <p className="text-[12px] text-destructive">{failed}</p>
      </section>
    );
  }

  return (
    <section className="space-y-3 rounded-lg border border-border p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Library className="size-4 text-muted-foreground" aria-hidden />
          <h2 className="text-[13px] font-semibold">Collections</h2>
        </div>
        <button
          type="button"
          onClick={startNew}
          disabled={busy}
          className="rounded-md border border-border px-2.5 py-1 text-[12px] hover:bg-muted disabled:opacity-50"
        >
          New collection
        </button>
      </div>

      <p className="text-[12px] text-muted-foreground">
        The shelves the library is arranged into. A collection carries no access decision — who can
        read an article is set on the article itself.
      </p>

      {shelves.length === 0 ? (
        <p className="text-[12px] text-muted-foreground">
          No collections yet. An article needs a shelf to sit on, so this is the first thing to
          create.
        </p>
      ) : (
        <ul className="space-y-1">
          {shelves.map((shelf) => (
            <li
              key={shelf.id}
              className={cn(
                "rounded border border-border px-2.5 py-2",
                shelf.isActive ? "" : "bg-muted/40",
              )}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="min-w-0">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="truncate text-[12px] font-medium">{shelf.name}</span>
                    {shelf.isActive ? null : (
                      <span className="rounded border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">
                        Hidden
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                    /kb/{shelf.slug} · {shelf.articleCount}{" "}
                    {shelf.articleCount === 1 ? "article" : "articles"} · {shelf.publishedCount}{" "}
                    published
                  </span>
                </span>
                <span className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => startEdit(shelf)}
                    disabled={busy}
                    className="rounded border border-border px-2 py-1 text-[11px] hover:bg-muted disabled:opacity-50"
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    onClick={() => void setActive(shelf, !shelf.isActive)}
                    disabled={busy}
                    className="rounded border border-border px-2 py-1 text-[11px] hover:bg-muted disabled:opacity-50"
                  >
                    {shelf.isActive ? "Hide" : "Put back"}
                  </button>
                </span>
              </div>

              {editingId === shelf.id ? (
                <CollectionForm
                  name={name}
                  description={description}
                  order={order}
                  slugNote={`Its address stays /kb/${shelf.slug} — renaming a shelf never moves it, so links people have already shared keep working.`}
                  busy={busy}
                  onName={setName}
                  onDescription={setDescription}
                  onOrder={setOrder}
                  onSave={() => void save()}
                  onCancel={() => setEditingId(null)}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {editingId === "new" ? (
        <div className="rounded border border-border px-2.5 py-2">
          <CollectionForm
            name={name}
            description={description}
            order={order}
            slugNote="The address is worked out from the name once, at creation, and never changes afterwards."
            busy={busy}
            onName={setName}
            onDescription={setDescription}
            onOrder={setOrder}
            onSave={() => void save()}
            onCancel={() => setEditingId(null)}
          />
        </div>
      ) : null}

      {failed !== null ? <p className="text-[12px] text-destructive">{failed}</p> : null}
    </section>
  );
}

function CollectionForm({
  name,
  description,
  order,
  slugNote,
  busy,
  onName,
  onDescription,
  onOrder,
  onSave,
  onCancel,
}: {
  name: string;
  description: string;
  order: string;
  slugNote: string;
  busy: boolean;
  onName: (value: string) => void;
  onDescription: (value: string) => void;
  onOrder: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="mt-2 space-y-2 border-t border-border pt-2">
      <Field label="Name">
        <input
          value={name}
          onChange={(e) => onName(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-base sm:text-[12px]"
        />
      </Field>
      <Field label="Description" hint="One line, shown under the shelf in the library.">
        <input
          value={description}
          onChange={(e) => onDescription(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-base sm:text-[12px]"
        />
      </Field>
      <Field label="Position" hint="Lower numbers sit higher. Ties break on name.">
        <input
          value={order}
          onChange={(e) => onOrder(e.target.value)}
          inputMode="numeric"
          className="w-24 rounded-md border border-border bg-background px-2 py-1.5 text-base sm:text-[12px]"
        />
      </Field>
      <p className="text-[11px] text-muted-foreground">{slugNote}</p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={busy || name.trim().length === 0}
          className="rounded-md bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground disabled:opacity-50"
        >
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-md border border-border px-3 py-1.5 text-[12px] hover:bg-muted disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Who the knowledge base exists for.
 *
 * ⚠️ **The pilot is picked by NAME, and the previous version of this screen took raw guids in a
 * textarea.** That is not a smaller version of the same control: a list of guids cannot be
 * checked, so a pilot nobody can read is a pilot nobody can correct, and the one person who
 * needs taking out is indistinguishable from the rest. The picker searches
 * `/api/tickets/requester-candidates`, which is already bounded to three characters, twenty
 * results, no cursor and active accounts only (D-51) — nothing here widens it.
 *
 * ⚠️ **A refusal, an empty result and a failure are three answers** (D-122). That lookup is
 * gated on being able to name somebody else, which every `kb.manage` holder does hold today —
 * so a 403 means the two gates have come apart, and saying so is more use than an empty list
 * that reads as "nobody by that name works here".
 */
function RolloutPanel() {
  const [rollout, setRollout] = useState<KnowledgeRollout | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [term, setTerm] = useState("");
  const [results, setResults] = useState<RequesterCandidate[] | null>(null);
  const [lookupFailed, setLookupFailed] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    void getKnowledgeRollout()
      .then(setRollout)
      .catch(() => setFailed("The rollout could not be loaded."));
  }, []);

  // ⚠️ On submit, never per keystroke. It is a staff lookup rather than a filter over a list
  // already in the browser (D-117's distinction), and every call spends the search rate limit.
  const search = async () => {
    const q = term.trim();
    if (q.length < 3) {
      setLookupFailed("Type at least three characters.");
      setResults(null);
      return;
    }

    setSearching(true);
    setLookupFailed(null);
    try {
      const found = await searchRequesterCandidates(q);
      setResults(found.items);
    } catch (error: unknown) {
      setResults(null);
      setLookupFailed(
        error instanceof ApiError && error.status === 403
          ? "This account cannot look people up, so the pilot list cannot be edited from here."
          : "That search could not be run.",
      );
    } finally {
      setSearching(false);
    }
  };

  const apply = async (mode: KnowledgeRolloutMode, memberUserIds: string[]) => {
    setBusy(true);
    setFailed(null);
    try {
      setRollout(await setKnowledgeRollout({ mode, memberUserIds }));
    } catch (error: unknown) {
      setFailed(error instanceof ApiError ? error.message : "That could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  if (rollout === null) {
    return failed === null ? null : (
      <section className="rounded-lg border border-border p-4">
        <p className="text-[12px] text-destructive">{failed}</p>
      </section>
    );
  }

  const ids = rollout.members.map((m) => m.userId);
  const add = (userId: string) => {
    if (ids.some((existing) => sameUserId(existing, userId))) return;
    void apply(rollout.mode, [...ids, userId]);
  };
  const remove = (userId: string) =>
    void apply(
      rollout.mode,
      ids.filter((existing) => !sameUserId(existing, userId)),
    );

  return (
    <section className="space-y-3 rounded-lg border border-border p-4">
      <div className="flex items-center gap-2">
        <Rocket className="size-4 text-muted-foreground" aria-hidden />
        <h2 className="text-[13px] font-semibold">Who can see the knowledge base</h2>
      </div>

      <p className="text-[12px] text-muted-foreground">
        The service desk always sees it — somebody has to write the articles before anybody is told
        it exists. This decides who else does. Switching it off does not delete anything, and moving
        to everyone does not clear the pilot list.
      </p>

      <div className="flex flex-wrap gap-2">
        {ROLLOUT_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => void apply(mode, ids)}
            disabled={busy}
            className={cn(
              "rounded-md border px-3 py-1.5 text-[12px] font-medium disabled:opacity-50",
              rollout.mode === mode
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border hover:bg-muted",
            )}
          >
            {mode === "Off" ? "Nobody yet" : mode === "Pilot" ? "A pilot group" : "Everyone"}
          </button>
        ))}
      </div>

      {rollout.mode === "Pilot" || rollout.members.length > 0 ? (
        <div className="space-y-3 rounded-md border border-border/70 bg-muted/30 p-3">
          <div>
            <p className="text-[12px] font-medium">
              The pilot · {rollout.members.length}{" "}
              {rollout.members.length === 1 ? "person" : "people"}
            </p>
            {/* ⚠️ People, never teams — a team quietly grows the pilot every time somebody
                joins it, so the list would change without anybody deciding it should. */}
            <p className="text-[11px] text-muted-foreground">
              Named people, never teams: a team would grow the pilot every time somebody joined it,
              without anybody deciding it should.
            </p>
          </div>

          {rollout.members.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">
              Nobody yet. While the mode is “a pilot group” and this list is empty, only the service
              desk sees the knowledge base.
            </p>
          ) : (
            <ul className="space-y-1">
              {rollout.members.map((member) => (
                <li
                  key={member.userId}
                  className="flex items-center justify-between gap-2 rounded border border-border bg-background px-2 py-1.5"
                >
                  <span className="min-w-0">
                    <span
                      className={cn(
                        "block truncate text-[12px]",
                        member.isKnown ? "" : "text-muted-foreground italic",
                      )}
                    >
                      {member.displayName}
                    </span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {member.isKnown
                        ? [member.jobTitle, member.isActive ? null : "Deactivated"]
                            .filter(Boolean)
                            .join(" · ") || member.userId
                        : member.userId}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(member.userId)}
                    disabled={busy}
                    className="shrink-0 rounded border border-border px-2 py-1 text-[11px] hover:bg-muted disabled:opacity-50"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-end gap-2">
            <Field label="Add somebody" hint="Search by name. Three characters or more.">
              <input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void search();
                  }
                }}
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-base sm:text-[12px]"
              />
            </Field>
            <button
              type="button"
              onClick={() => void search()}
              disabled={searching}
              className="mb-0.5 rounded-md border border-border px-3 py-1.5 text-[12px] hover:bg-muted disabled:opacity-50"
            >
              {searching ? "Searching…" : "Search"}
            </button>
          </div>

          {lookupFailed !== null ? (
            <p className="text-[12px] text-destructive">{lookupFailed}</p>
          ) : results !== null && results.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">Nobody matched that.</p>
          ) : results !== null ? (
            <ul className="space-y-1">
              {results.map((person) => {
                const already = ids.some((existing) => sameUserId(existing, person.userId));

                return (
                  <li
                    key={person.userId}
                    className="flex items-center justify-between gap-2 rounded border border-border bg-background px-2 py-1.5"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[12px]">{person.displayName}</span>
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {[person.jobTitle, person.siteName].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => add(person.userId)}
                      disabled={busy || already}
                      className="shrink-0 rounded border border-border px-2 py-1 text-[11px] hover:bg-muted disabled:opacity-50"
                    >
                      {already ? "In the pilot" : "Add"}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}

      {failed !== null ? <p className="text-[12px] text-destructive">{failed}</p> : null}
    </section>
  );
}
